import React from "react";
import Title from "../../ui/Title";

const KeySupport = () => {
  return (
    <div id="key-support" className="scroll-mt-24">
      <Title
        title="Developing a better understanding of mental health: A key to accessing support"
        className="mb-6"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          According to a{" "}
          <a
            href="https://www.who.int/news-room/fact-sheets/detail/mental-health-strengthening-our-response"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            fact sheet
          </a>{" "}
          by the World Health Organization (WHO), mental health is “a state of
          mental well-being that enables people to cope with the stresses of
          life, realize their abilities, learn well and work well, and
          contribute to their community.” Mental health is also a basic human
          right and is key for personal and socio-economic development. The WHO
          also defines mental health as something that is “more than just the
          absence of mental disorders and exists on a continuum or range, from
          positive, healthy functioning at one end to severe symptoms of mental
          health conditions at the other.” It's important to note that various
          individual, social, and structural determinants may protect or
          undermine mental health throughout someone’s life.
        </p>
        <div className="flex flex-col items-center justify-center my-6">
          <img
            src="/assets/img/health-workplace.png"
            alt="Mental Health Continuum"
            className=""
          />
          <p className="mt-2 italic">
            Source: United Nations Workplace Mental Health and Wellbeing{" "}
            <a
              href="https://www.un.org/en/healthy-workforce/files/Understanding%20Mental%20Health.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              strategy
            </a>
          </p>
        </div>
        <p>
          The United Nations’s Workplace Mental Health and Wellbeing{" "}
          <a
            href="https://www.un.org/en/healthy-workforce/files/Understanding%20Mental%20Health.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Strategy
          </a>{" "}
          illustrates the continuum of mental health. Here, the green end of the
          continuum indicates that someone is well and has high levels of
          well-being and resilience. The yellow area represents a decline in
          mental health, where people experience difficulty coping. Moving to
          the orange area symptoms might increase and coping becomes more
          difficult, and in the red area, one may experience severe distress and
          a severe impact on functioning.
        </p>
        <p>
          According to the U.S.{" "}
          <a
            href="https://www.cdc.gov/mental-health/about/index.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            Centers for Disease Control & Prevention
          </a>{" "}
          (CDC), people can experience challenges to their mental health even if
          they do not have a diagnosable mental health condition. The ambiguity
          between the two prevents people from seeking the support they need and
          contributes to the stigma around mental health conditions.
        </p>
        <p>
          Therefore, it is of utmost importance to increase mental health
          literacy in society as well as among healthcare providers and
          policymakers. There should be active efforts to address the
          misconceptions about mental health and mental healthcare, and support
          should be made accessible to everyone, regardless of whether they have
          a diagnosed mental health condition.
        </p>
        <p className="italic">
          Note: According to the{" "}
          <a
            href="https://www.who.int/news-room/fact-sheets/detail/mental-disorders"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            WHO
          </a>
          , mental disorders may also be referred to as mental health
          conditions, which is a “broader term covering mental disorders,
          psychosocial disabilities, and (other) mental states associated with
          significant distress, impairment in functioning, or risk of
          self-harm.” Different countries and organizations use varied terms
          including mental disorders, mental health conditions, mental illness,
          etc. This document primarily uses the term 'mental health conditions',
          but in sections where information/data is quoted, the term used in the
          original source has been retained.
        </p>
      </div>

      <Title
        title="Mental health for all: The way forward"
        className="mb-6 mt-12"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          In order to have an effective mental health support system, one of the
          key changes needed in society is developing the understanding that
          mental health is for all. Often, mental health support is perceived as
          something to be only considered when there is a severe mental health
          condition.
        </p>
        <ul className="space-y-2 list-disc list-outside ml-6">
          <li>
            During a co-design session conducted by the Caregiving Lab with
            caregivers in early 2024, we gathered several insights that
            reinforced this understanding. We learned that support services like
            counseling, especially at a healthcare facility, were perceived as
            something associated with being a patient. For caregivers who carry
            lasting trauma from full-time caregiving, being labeled a “patient”
            is deeply aversive.
          </li>
          <li>
            There is also a fear of judgment from the community and from one’s
            own family about availing mental health services. The stigma and the
            fear of being labeled mentally ill prevents many from accessing the
            support they need.
          </li>
          <li>
            Caregivers also find it difficult to express their emotional
            difficulties and seek support from family or friends. Fear of being
            perceived as weak and the fear of burdening others, keeps them from
            sharing the difficulties they were facing.
          </li>
          <li>
            Limited accessibility and awareness about affordable mental health
            services present another major challenge. Mental healthcare is often
            perceived as something that is unaffordable or available only to the
            elite. In our research, we found that even when caregivers felt that
            they needed support, they felt that availing of support like
            counseling was unaffordable. While there are government services
            that provide free or affordable mental health services (eg. Tele
            MANAS in India), awareness about such interventions is low.
          </li>
          <li>
            In rural areas, the need is blatantly obvious but people do not
            consider themselves worthy of interventions. Mental health is often
            correlated to the stress arising from not being financially stable
            or in debt. This points to a pressing need for change to improve
            awareness in the community and help develop a better understanding
            of mental health.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default KeySupport;
